import { Injectable } from "@angular/core";
import { toast } from "ngx-sonner";
import { ToastConfig } from "../types/toast.types";

/**
 * TOAST SERVICE
 *
 * Wrapper around ngx-sonner for displaying toast notifications.
 * Provides consistent styling and behavior across the application.
 *
 * This service is primarily used by the ToastInterceptor for automatic toasts,
 * but can also be used directly in components for manual toast notifications.
 *
 * Example:
 * ```typescript
 * constructor(private toastService: ToastService) {}
 *
 * handleComplexOperation() {
 *   try {
 *     // ... operation
 *     this.toastService.success('Operazione completata con successo');
 *   } catch (error) {
 *     this.toastService.error('Operazione fallita');
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: "root" })
export class ToastService {
  private readonly defaultDuration = 2000;

  /**
   * Display a success toast notification.
   *
   * @param message - Message to display
   * @param description - Optional description to display below the message
   * @param duration - Duration in milliseconds (default: 2000)
   */
  success(
    message: string,
    description?: string,
    duration = this.defaultDuration,
  ): void {
    toast.success(message, { description, duration });
  }

  /**
   * Display an error toast notification.
   *
   * @param message - Error message to display
   * @param duration - Duration in milliseconds (default: 2000)
   */
  error(message: string, duration = this.defaultDuration): void {
    toast.error(message, { duration });
  }

  /**
   * Display an info toast notification.
   *
   * @param message - Info message to display
   * @param duration - Duration in milliseconds (default: 2000)
   */
  info(message: string, duration = this.defaultDuration): void {
    toast.info(message, { duration });
  }

  /**
   * Display a warning toast notification.
   *
   * @param message - Warning message to display
   * @param duration - Duration in milliseconds (default: 2000)
   */
  warning(message: string, duration = this.defaultDuration): void {
    toast.warning(message, { duration });
  }

  /**
   * Display a custom toast with advanced configuration.
   *
   * @param config - Toast configuration object
   */
  custom(config: ToastConfig): void {
    const duration = config.duration || this.defaultDuration;

    if (config.title) {
      toast(config.title, {
        description: config.description,
        duration,
      });
    } else {
      toast(config.description, { duration });
    }
  }

  /**
   * Display a promise toast that shows loading/success/error states.
   *
   * Useful for long-running operations where you want to show progress.
   *
   * Example:
   * ```typescript
   * this.toastService.promise(
   *   this.http.post(url, data).toPromise(),
   *   {
   *     loading: 'Caricamento in corso...',
   *     success: 'Operazione completata!',
   *     error: 'Operazione fallita'
   *   }
   * );
   * ```
   */
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    },
  ): Promise<T> {
    toast.promise(promise, messages);
    return promise;
  }
}
